import type { CaseStatus, Disposition, Role } from "../api/types.ts"

export type CaseActionId =
  | "cancel"
  | "arrive"
  | "inspect"
  | "weight"
  | "freight"
  | "confirmArrival"
  | "disposition"
  | "confirmDoc"
  | "requestReview"
  | "discard"

/**
 * 누구 일인가 — 화주가 할 일과 쿨트랙JP가 할 일.
 *
 * 관리자는 두 쪽을 함께 보므로 화면이 이것으로 갈라 보여 준다(`NextActions.tsx`).
 * 권한과는 다른 값이다: 관리자는 둘 다 누를 수 있지만, 일은 여전히 둘 중 한쪽 것이다.
 */
export type CaseActionOwner = "shipper" | "warehouse"

export type CaseAction = {
  id: CaseActionId
  owner: CaseActionOwner
  tone: "primary" | "secondary" | "danger"
  /** 비어 있지 않으면 잠긴다. 서버가 준 blocker 코드를 그대로 담는다. */
  blockedBy: string[]
}

/**
 * 지금 이 사람이 이 건에 할 수 있는 일.
 *
 * 화면 곳곳의 조건식을 여기 하나로 모았다. 순서가 곧 화면에 놓이는 순서이고,
 * 앞에 오는 것이 그 역할의 주 동작이다. 두 쪽 일을 함께 보는 관리자에게는 화면이
 * `owner` 로 묶어 보여 주고, 묶음 안에서는 이 순서를 지킨다.
 */
export function availableCaseActions(input: {
  role: Role
  status: CaseStatus
  shipperConfirmedAt: string | null
  disposition: Disposition | null
  /** 접수 라인 수. 0 이면 셀 것이 없는 옛 건이다. */
  lineCount: number
  inspectedAt: string | null
  blockers: string[]
}): CaseAction[] {
  const { role, status, shipperConfirmedAt, disposition, lineCount, inspectedAt, blockers } = input
  const isShipper = role === "shipper" || role === "admin"
  const isWarehouse = role === "warehouse" || role === "admin"
  // 입고완료(arrived)에서만 내용을 고칠 수 있다. 출고된 뒤에는 서버가 막는다.
  const editable = status === "arrived"

  const actions: CaseAction[] = []

  // 반송장번호가 없어 스캔으로 찾을 수 없는 건은 손으로 입고한다.
  if (isWarehouse && status === "registered") {
    actions.push({ id: "arrive", owner: "warehouse", tone: "primary", blockedBy: [] })
  }

  if (isWarehouse && editable) {
    // 셀 줄이 있으면 검수가 이 사람의 주 동작이다. 다시 세는 것도 허용한다(틀리게 셀 수 있다).
    if (lineCount > 0) {
      actions.push({
        id: "inspect",
        owner: "warehouse",
        tone: inspectedAt ? "secondary" : "primary",
        blockedBy: [],
      })
    }
    actions.push({ id: "weight", owner: "warehouse", tone: "secondary", blockedBy: [] })
    actions.push({ id: "freight", owner: "warehouse", tone: "secondary", blockedBy: [] })
  }

  if (isShipper && editable) {
    // 실물을 본 쿨트랙JP가 적은 내용을 화주가 확인해 서류를 확정한다.
    actions.push({ id: "confirmDoc", owner: "shipper", tone: "primary", blockedBy: blockers })
    if (!shipperConfirmedAt) {
      actions.push({ id: "confirmArrival", owner: "shipper", tone: "secondary", blockedBy: [] })
    }
    actions.push({ id: "disposition", owner: "shipper", tone: "secondary", blockedBy: [] })
    actions.push({ id: "requestReview", owner: "shipper", tone: "secondary", blockedBy: [] })
  }

  if (isWarehouse && editable && disposition === "discard") {
    actions.push({ id: "discard", owner: "warehouse", tone: "danger", blockedBy: [] })
  }

  if (isShipper && status === "registered") {
    actions.push({ id: "cancel", owner: "shipper", tone: "danger", blockedBy: [] })
  }

  return actions
}
